/**
 * Namespace Keys List Component
 *
 * Displays all cache keys for a selected namespace with pagination.
 * Allows clicking on keys to view their cached content or delete them.
 */

import { useState, useMemo } from 'react';
import {
  RefreshCw,
  Eye,
  ChevronDown,
  Search,
  Database,
  X,
  Trash2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { CacheContentDialog } from './cache-content-dialog';
import { useNamespaceKeys, useDeleteCacheKey } from '@/hooks/use-cache-management';
import type { CacheNamespace, CacheEntry } from '@/types/cache';
import { CACHE_NAMESPACE_LABELS, CACHE_NAMESPACE_COLORS, formatTTL } from '@/types/cache';

interface NamespaceKeysListProps {
  namespace: CacheNamespace;
  onClose: () => void;
}

/**
 * Get TTL badge variant based on remaining time
 */
function getTtlBadgeColor(ttlSeconds: number): string {
  if (ttlSeconds === -1) {
    return 'bg-gray-100 text-gray-700';
  }
  if (ttlSeconds < 300) {
    // < 5 minutes - expiring soon
    return 'bg-yellow-100 text-yellow-800';
  }
  return 'bg-green-100 text-green-800';
}

/**
 * Namespace Keys List
 * Displays all keys for a selected namespace with pagination and search
 */
export function NamespaceKeysList({ namespace, onClose }: NamespaceKeysListProps) {
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedCacheKey, setSelectedCacheKey] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);

  const { toast } = useToast();
  const deleteKeyMutation = useDeleteCacheKey();

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, refetch, isRefetching } =
    useNamespaceKeys(namespace);

  // Flatten all pages into a single array of keys
  const allKeys = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.keys);
  }, [data]);

  // Get total count from first page
  const totalCount = data?.pages?.[0]?.totalCount ?? 0;

  // Filter keys based on search input
  const filteredKeys = useMemo(() => {
    if (!searchFilter.trim()) return allKeys;
    const lowerSearch = searchFilter.toLowerCase();
    return allKeys.filter(key => key.key.toLowerCase().includes(lowerSearch));
  }, [allKeys, searchFilter]);

  const handleViewContent = (key: string) => {
    setSelectedCacheKey(key);
    setDialogOpen(true);
  };

  const handleDeleteClick = (key: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from triggering
    setKeyToDelete(key);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!keyToDelete) return;

    try {
      await deleteKeyMutation.mutateAsync({ key: keyToDelete, namespace });
      toast({
        title: 'Cache key deleted',
        description: 'The cache key has been successfully deleted.',
      });
      setDeleteDialogOpen(false);
      setKeyToDelete(null);
    } catch (error) {
      toast({
        title: 'Failed to delete cache key',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  const namespaceLabel = CACHE_NAMESPACE_LABELS[namespace];
  const namespaceColor = CACHE_NAMESPACE_COLORS[namespace];

  return (
    <Card className="animate-in slide-in-from-top-2 fade-in duration-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <span>{namespaceLabel} Keys</span>
            </CardTitle>
            <Badge className={namespaceColor}>{totalCount.toLocaleString()} keys</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading || isRefetching}
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isRefetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Search Filter */}
        <div className="relative mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter keys..."
            value={searchFilter}
            onChange={e => setSearchFilter(e.target.value)}
            className="pl-9"
          />
          {searchFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearchFilter('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {/* Loading State */}
        {isLoading && (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && allKeys.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Database className="h-12 w-12 mb-4 opacity-50" />
            <p className="font-medium">No cached keys in this namespace</p>
            <p className="text-sm mt-1">
              Keys will appear here once data is cached by the application.
            </p>
          </div>
        )}

        {/* No Search Results */}
        {!isLoading && allKeys.length > 0 && filteredKeys.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Search className="h-12 w-12 mb-4 opacity-50" />
            <p className="font-medium">No matching keys</p>
            <p className="text-sm mt-1">
              No keys match "{searchFilter}". Try a different search term.
            </p>
          </div>
        )}

        {/* Keys List */}
        {!isLoading && filteredKeys.length > 0 && (
          <>
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredKeys.map((entry: CacheEntry, index: number) => (
                  <div
                    key={entry.key}
                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors cursor-pointer group"
                    style={{ animationDelay: `${Math.min(index * 20, 200)}ms` }}
                    onClick={() => handleViewContent(entry.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewContent(entry.key);
                      }
                    }}
                  >
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-sm font-mono truncate" title={entry.key}>
                        {entry.key}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {entry.cached && (
                        <Badge variant="outline" className={getTtlBadgeColor(entry.ttlSeconds)}>
                          TTL: {formatTTL(entry.ttlSeconds)}
                        </Badge>
                      )}
                      {!entry.cached && (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          Expired
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-60 hover:opacity-100 hover:text-destructive transition-colors"
                        onClick={e => handleDeleteClick(entry.key, e)}
                        title="Delete cache key"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Eye className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4 mr-2" />
                      Load More ({totalCount - allKeys.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Loaded All */}
            {!hasNextPage && allKeys.length > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4 pt-4 border-t">
                Showing all {allKeys.length.toLocaleString()} keys
              </p>
            )}
          </>
        )}
      </CardContent>

      {/* Cache Content Dialog */}
      <CacheContentDialog
        cacheKey={selectedCacheKey}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Cache Key
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <span className="block">Are you sure you want to delete this cache key?</span>
              {keyToDelete && (
                <code
                  className="block text-xs font-mono bg-muted p-2 rounded truncate"
                  title={keyToDelete}
                >
                  {keyToDelete}
                </code>
              )}
              <span className="block text-destructive/80">
                This action cannot be undone. The cached data will need to be regenerated by the
                application.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteKeyMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteKeyMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteKeyMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
